import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { AccountingApi, MainApi } from '_api';
import { FIELD_KEYS } from '_constants/forms';
import { MODAL_KEYS } from '_constants/modals';
import { TASK_STATUS } from '_constants/tasks';
import { handleError, handleResponse } from '_helpers/api';
import { hideModal } from '_store/modals/slice';

import { selectForm, selectParams } from './selector';

export const getTasks = createAsyncThunk(
  'tasks/getTasks',
  async function ({ customerId, orderId }, { getState, rejectWithValue }) {
    try {
      const params = selectParams(getState());

      const response = await AccountingApi.getTasks({
        customerId,
        orderId,
        ...params,
      });

      return handleResponse(response);
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const updateTask = createAsyncThunk(
  'tasks/updateTasks',
  async function ({ customerId, orderId, productId, taskId, body = {} }, { getState, dispatch, rejectWithValue }) {
    try {
      const form = selectForm(getState());

      await MainApi.updateTask({
        orderId,
        productId,
        taskId,
        body: { ...body, ...form },
      });

      dispatch(resetForm());
      dispatch(hideModal({ key: MODAL_KEYS.taskResponseSend }));

      const params = selectParams(getState());

      if (params[FIELD_KEYS.page] === 1) {
        dispatch(getTasks({ customerId, orderId }));
      } else {
        dispatch(resetParams());
      }
    } catch (e) {
      return rejectWithValue(handleError(e));
    }
  }
);

export const initialParams = {
  [FIELD_KEYS.page]: 1,
  [FIELD_KEYS.count]: 3,
  [FIELD_KEYS.status]: TASK_STATUS.new,
};

export const initialForm = {
  [FIELD_KEYS.response]: '',
};

const tasksSlice = createSlice({
  name: 'tasks',
  initialState: {
    form: initialForm,
    params: initialParams,
    data: {},
    error: null,
    fetchCounter: 0,
    loading: 0,
    updating: false,
  },
  reducers: {
    refreshForm(state, action) {
      state.form = {
        ...state.form,
        ...action.payload,
      };
    },
    resetForm(state) {
      state.form = {
        ...initialForm,
      };
    },
    refreshParams(state, action) {
      state.params = {
        ...state.params,
        ...action.payload,
      };
    },
    resetParams(state) {
      state.params = {
        ...state.params,
        ...initialParams,
      };
    },
    resetData(state) {
      state.data = {};
    },
    resetError(state) {
      state.error = null;
    },
    resetFetchCounter(state) {
      state.fetchCounter = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTasks.pending, (state) => {
        state.error = null;
        state.loading += 1;
      })
      .addCase(getTasks.fulfilled, (state, action) => {
        state.data = action.payload;
        state.fetchCounter += 1;
        state.loading -= 1;
      })
      .addCase(getTasks.rejected, (state, action) => {
        state.error = action.payload;
        state.loading -= 1;
      });

    builder
      .addCase(updateTask.pending, (state) => {
        state.error = null;
        state.updating = true;
      })
      .addCase(updateTask.fulfilled, (state) => {
        state.updating = false;
      })
      .addCase(updateTask.rejected, (state, action) => {
        state.error = action.payload;
        state.updating = false;
      });
  },
});

export const { refreshForm, resetForm, refreshParams, resetParams, resetData, resetError, resetFetchCounter } =
  tasksSlice.actions;

export default tasksSlice.reducer;
